// Retries for the editor's writes (R41). withRetry asks a policy what to do after each failure; a test passes its own sleep and jitter.
import {ApiError, NoResponseError} from 'repository/errors';

const defaultSleep = ms => new Promise(resolve => setTimeout(resolve, ms));
// Up to 20 percent either way, so that retries that began together do not all land together.
const defaultJitter = delay => Math.round(delay * (0.8 + Math.random() * 0.4));

/**
 * Runs fn(). When it rejects, policy(error, attempt) answers the delay in milliseconds before the next attempt (attempt counts from 0): a
 * finite number, 0 or more. Any other answer stops, false being the usual one, and then the error is thrown. The sleep is for jitter(delay).
 */
export const withRetry = async (fn, policy, {sleep = defaultSleep, jitter = defaultJitter} = {}) => {
    for (let attempt = 0; ; attempt += 1) {
        try {
            return await fn();
        } catch (error) {
            const delay = policy(error, attempt);
            if (!(Number.isFinite(delay) && delay >= 0)) {
                throw error;
            }
            await sleep(jitter(delay));
        }
    }
};

// How long a request that failed at once is given to land (or fail) on the server before the map is read again (the Fable ruling, R7). A request the
// client lost to a dropped connection may still have been delivered, and it commits within the server's normal lifetime: edge forwarding, the FPM
// pick-up, up to five seconds at the project's lock, the write. Ten seconds cover that on a healthy server, and let the read see the commit and not the
// state before it. A request that timed out is a minute old already, so it is not held.
export const HOLD_AFTER_LOST_MS = 10000;

/**
 * Waits `ms` with the sleep of withRetry's options, so that the tests' instant sleep serves the hold as well as the retries, and a real timer otherwise.
 * No jitter: a hold is a fixed wait.
 */
export const sleepFor = (retryOptions, ms) => (retryOptions?.sleep ?? defaultSleep)(ms);

/**
 * Map and axis saves: a 409 project_locked is retried three times, after 1, 2 and 4 s, and a request that got no response once, after 1 s.
 * The two counts are independent. Every call returns a fresh policy, so one save never spends another's retries.
 */
export const savePolicy = () => {
    let locked = 0;
    let noResponse = 0;
    return error => {
        if (error instanceof ApiError && error.is('project_locked')) {
            return [1000, 2000, 4000][locked++] ?? false;
        }
        if (error instanceof NoResponseError) {
            return noResponse++ === 0 ? 1000 : false;
        }
        return false;
    };
};

/** Undo and favourite: a 409 project_locked is retried once, after 1 s. A request that got no response is not retried. */
export const ONE_RETRY_LOCKED = (error, attempt) => (error instanceof ApiError && error.is('project_locked') && attempt === 0 ? 1000 : false);

export const NO_RETRY = () => false;
